/**
 * Move handle of a multiple selection: moves every selected entity at once.
 *
 * Centered below the shared bounding box, at a fixed pixel distance — the
 * mirror of the rotation handle above it — so that it never sits on one of the
 * entities' own handles, drawn at the same time on their own grids.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { KeyPoints } from "../types";
import { TranslationHandle } from "./TranslationHandle";
import { HANDLE_OFFSET_PIXELS, HANDLE_SIZES, TRANSFORM_THEME } from "../types";

export class GroupTranslationHandle extends TranslationHandle {
  private offsetInPx = HANDLE_OFFSET_PIXELS * HANDLE_SIZES.handleScale;
  private worldOffset: number;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityIds: DrawingModule.OdDbObjectId[],
    keyPoints: KeyPoints,
  ) {
    super(viewer, history, entityIds, keyPoints);

    this.worldOffset = this.viewer.screenDistanceToWorld(this.offsetInPx);
    this.worldPosition = {
      x: keyPoints.bottomMiddle.x,
      y: keyPoints.min.y - this.worldOffset,
      z: keyPoints.center.z,
    };

    // Same blue as the bounding box it belongs to, the move icon in white,
    // tied to the middle of the box's bottom edge by a dashed line.
    this.drawOptions = {
      fillColor: TRANSFORM_THEME.gridPerimeter,
      strokeColor: TRANSFORM_THEME.handleStroke,
      radius: HANDLE_SIZES.translation * HANDLE_SIZES.handleScale,
      icon: "move",
      innerIcon: "move",
      tether: keyPoints.bottomMiddle,
    };
  }

  /** Keeps the pixel distance to the box after a zoom. */
  beforeRedraw(): void {
    // The drag measures the pointer from this position: it must not move under it.
    if (this.isTransforming) return;
    const previousWorldOffset = this.worldOffset;
    this.worldOffset = this.viewer.screenDistanceToWorld(this.offsetInPx);
    this.worldPosition.y -= this.worldOffset - previousWorldOffset;
  }
}
